import {
  CustomFieldDef,
  useCreateCustomFieldDefMutation,
  useDeleteCustomFieldDefMutation,
  useUpdateCustomFieldDefMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseCustomFieldDefMutationProps = {}

export type UseCustomFieldDefMutationResult = {
  createCustomFieldDef: (customFieldDef: Partial<CustomFieldDef>, callback: EntityMutationCallback<CustomFieldDef> | undefined) => void
  updateCustomFieldDef: (customFieldDef: Partial<CustomFieldDef>, callback: EntityMutationCallback<CustomFieldDef> | undefined) => void
  deleteCustomFieldDef: (customFieldDefId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useCustomFieldDefMutation = (props: UseCustomFieldDefMutationProps = {}): UseCustomFieldDefMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createCustomFieldDef, {isLoading: isCreating}] = useCreateCustomFieldDefMutation()
  const [_updateCustomFieldDef, {isLoading: isUpdating}] = useUpdateCustomFieldDefMutation()
  const [_deleteCustomFieldDef, {isLoading: isDeleting}] = useDeleteCustomFieldDefMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useCustomFieldDefUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.customfielddef", "Custom Field Definition"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldDefUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useCustomFieldDefUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.customfielddef", "Custom Field Definition"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldDefUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useCustomFieldDefUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.customfielddef", "Custom Field Definition"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldDefUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<CustomFieldDef>, callback: EntityMutationCallback<CustomFieldDef> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfielddef", "Custom Field Definition") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<CustomFieldDef>, callback: EntityMutationCallback<CustomFieldDef> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfielddef", "Custom Field Definition") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfielddef", "Custom Field Definition") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((customFieldDefId: string, callback: EntityMutationCallback<CustomFieldDef> | undefined) => {
    const promise = _deleteCustomFieldDef({id: customFieldDefId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteCustomFieldDef, handleDeleteResult])

  const createCustomFieldDef = useCallback((customFieldDef: Partial<CustomFieldDef>, callback: EntityMutationCallback<CustomFieldDef> | undefined) => {
    const promise = _createCustomFieldDef(customFieldDef).unwrap()
    handleCreateResult(promise, callback)
  }, [_createCustomFieldDef, handleCreateResult])

  const updateCustomFieldDef = useCallback((customFieldDef: Partial<CustomFieldDef>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateCustomFieldDef(customFieldDef).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateCustomFieldDef, handleUpdateResult])

  const deleteCustomFieldDef = useCallback((customFieldDefId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.customfielddef", "Custom Field Definition"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.customFieldDef.confirm", "Are you sure you want to delete this custom field definition?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(customFieldDefId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createCustomFieldDef: createCustomFieldDef,
    updateCustomFieldDef: updateCustomFieldDef,
    deleteCustomFieldDef: deleteCustomFieldDef,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
